// Left navigation rail.

import { useEffect, useRef, useState } from "react";
import { useAuth } from "../AuthContext";
import { useAppData, useMe, useScreenEnabled } from "../store";
import type { Screen } from "../types";
import { Ic, Mark } from "./Icon";
import { Avatar } from "./Primitives";

const ITEMS: ([null, string] | [Screen, string, string])[] = [
  ["inbox",     "Inbox",          "inbox"],
  ["upload",    "Upload",         "upload"],
  ["archive",   "Archive",        "archive"],
  ["docs",      "Documents",      "file"],
  ["anti",      "Anti‑portfolio", "anti"],
  ["sim",       "Simulator",      "sim"],
  ["knowledge", "Knowledge",      "knowledge"],
  [null, "Settings"],
  ["routes",       "Routes",        "route"],
  ["lists",        "Lists",         "list"],
  ["tags",         "Tags",          "tag"],
  ["enrichments",  "Enrichments",   "enrich"],
  ["team",         "Team",          "team"],
  ["integrations", "Integrations",  "plug"],
];

export function Rail({
  current, onNav,
}: { current: Screen; onNav: (s: Screen) => void }) {
  const { data, uploadJob } = useAppData();
  const me = useMe();
  const { signOut } = useAuth();
  const screenEnabled = useScreenEnabled();
  const [menuOpen, setMenuOpen] = useState(false);
  const footRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menuOpen) return;
    const close = (e: MouseEvent) => {
      if (!footRef.current?.contains(e.target as Node)) setMenuOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [menuOpen]);

  const activeCount =
    data?.deals.filter(d => !["passed", "rejected", "invested"].includes(d.status)).length ?? 0;
  const uploading = uploadJob?.phase === "working";

  return (
    <nav className="rail">
      <div className="rail-brand">
        <span className="mark"><Mark size={20} /></span>
        <span className="name">Prism</span>
      </div>
      {ITEMS.map((it, i) => {
        if (it[0] === null) return <div key={i} className="rail-section">{it[1]}</div>;
        const [id, label, icon] = it;
        if (!screenEnabled(id)) return null;
        const count = id === "inbox" ? activeCount : undefined;
        const spinning = id === "upload" && uploading;
        return (
          <a
            key={id}
            className={"rail-item" + (current === id ? " active" : "")}
            onClick={() => onNav(id)}
          >
            <Ic name={icon} />
            <span className="label">{label}</span>
            {spinning && <span className="rail-spin" />}
            {!spinning && count != null && <span className="count">{count}</span>}
          </a>
        );
      })}
      <div
        ref={footRef}
        className="rail-foot"
        onClick={() => setMenuOpen((o) => !o)}
        style={{ cursor: "pointer", position: "relative" }}
        title={me ? `${me.name} · ${me.email}` : undefined}
      >
        <Avatar id={me?.id} size="sm" />
        <span className="who">{data?.firm.name ?? "…"}</span>
        <span className="kbd">⌘K</span>
        {menuOpen && (
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              position: "absolute", bottom: "calc(100% + 8px)", left: 10, right: 10,
              background: "var(--surface)", border: "1px solid var(--border)",
              borderRadius: 8, boxShadow: "var(--shadow-pop)", padding: 10,
              zIndex: "var(--z-pop)" as never,
            }}
          >
            <div style={{ fontSize: 13, fontWeight: 600 }}>{me?.name}</div>
            <div style={{
              fontSize: 11.5, color: "var(--ink-3)", marginTop: 2,
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            }}>{me?.email}</div>
            <div style={{ fontSize: 11.5, color: "var(--ink-3)" }}>{me?.role}</div>
            <button
              className="btn ghost sm"
              style={{ width: "100%", marginTop: 10, justifyContent: "center" }}
              onClick={() => void signOut()}
            >
              <Ic name="x" size={12} /> Sign out
            </button>
          </div>
        )}
      </div>
    </nav>
  );
}
